/**
 * GLYDE Voice SDK - useVoiceAgent Hook
 *
 * React hook for managing voice agent lifecycle and state.
 *
 * @example
 * const { status, transcripts, start, stop, mute, setLanguage } = useVoiceAgent({
 *   publishableKey: 'pk_...',
 *   contextType: 'screening',
 *   contextId: 'application-uuid'
 * });
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { GlydeVoice } from '../GlydeVoice';
import type {
  GlydeVoiceConfig,
  VoiceEvent,
  Transcript,
  AgentMetadata,
  VoiceLanguageConfig,
  LanguageChangedPayload
} from '../core/types';

/**
 * Voice agent status
 */
export type VoiceAgentStatus = 'idle' | 'connecting' | 'active' | 'error';

/**
 * Voice agent hook configuration
 */
export interface UseVoiceAgentConfig extends Omit<GlydeVoiceConfig, 'onEvent' | 'onTranscript'> {
  /** Auto-start the voice agent on mount */
  autoStart?: boolean;
}

/**
 * Voice agent hook return value
 */
export interface UseVoiceAgentReturn {
  /** Current status of the voice agent */
  status: VoiceAgentStatus;
  /** Conversation transcripts */
  transcripts: Transcript[];
  /** Whether the user is currently speaking */
  isUserSpeaking: boolean;
  /** Whether the agent is currently speaking */
  isAgentSpeaking: boolean;
  /** Whether the microphone is muted */
  isMuted: boolean;
  /** Error message if status is 'error' */
  errorMessage: string | null;
  /** Agent metadata from voice config (agent_name, company_name, etc.) */
  agentMetadata: AgentMetadata | null;
  /** Resolved language/voice map from the server, or null on older APIs */
  languageConfig: VoiceLanguageConfig | null;
  /** Language whose voice is currently active ('en', 'es', ...), or null when unknown */
  activeLanguage: string | null;
  /** Start the voice session */
  start: () => Promise<void>;
  /** Stop the voice session */
  stop: () => void;
  /** Toggle mute state */
  toggleMute: () => void;
  /** Set mute state */
  setMuted: (muted: boolean) => void;
  /**
   * Set the agent's spoken language.
   * 'auto' re-enables detection-driven switching; a language code ('en', 'es')
   * pins the voice. No-op when the server exposed no language_config.
   */
  setLanguage: (mode: 'auto' | string) => void;
  /** Clear transcripts */
  clearTranscripts: () => void;
  /** The underlying GlydeVoice instance */
  voiceAgent: GlydeVoice | null;
}

/**
 * React hook for managing voice agent state and lifecycle
 *
 * @param config - Voice agent configuration
 * @returns Voice agent state and control methods
 */
export function useVoiceAgent(config: UseVoiceAgentConfig): UseVoiceAgentReturn {
  const [status, setStatus] = useState<VoiceAgentStatus>('idle');
  const [transcripts, setTranscripts] = useState<Transcript[]>([]);
  const [isUserSpeaking, setIsUserSpeaking] = useState(false);
  const [isAgentSpeaking, setIsAgentSpeaking] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [agentMetadata, setAgentMetadata] = useState<AgentMetadata | null>(null);
  const [languageConfig, setLanguageConfig] = useState<VoiceLanguageConfig | null>(null);
  const [activeLanguage, setActiveLanguage] = useState<string | null>(null);

  const voiceAgentRef = useRef<GlydeVoice | null>(null);

  /**
   * Handle voice events from the SDK
   */
  const handleVoiceEvent = useCallback((event: VoiceEvent) => {
    switch (event.type) {
      case 'ready':
        setStatus('active');
        break;
      case 'close':
        setStatus('idle');
        setIsUserSpeaking(false);
        setIsAgentSpeaking(false);
        break;
      case 'error':
        setStatus('error');
        setErrorMessage(
          typeof event.payload === 'object' && event.payload !== null && 'message' in event.payload
            ? String((event.payload as { message: string }).message)
            : 'Connection error'
        );
        break;
      case 'user_speaking':
        setIsUserSpeaking(Boolean(event.payload));
        break;
      case 'agent_speaking':
        setIsAgentSpeaking(Boolean(event.payload));
        break;
      case 'language_changed':
        setActiveLanguage(
          typeof event.payload === 'object' && event.payload !== null && 'language' in event.payload
            ? (event.payload as LanguageChangedPayload).language
            : null
        );
        break;
    }
  }, []);

  /**
   * Handle transcript from the SDK
   */
  const handleTranscript = useCallback((text: string, role: 'user' | 'agent') => {
    setTranscripts(prev => [...prev, {
      role,
      content: text,
      timestamp: new Date()
    }]);
  }, []);

  /**
   * Start the voice session.
   * Stops any existing instance first to prevent duplicate WebSocket connections
   * and microphone streams that can cause missing user transcripts.
   */
  const start = useCallback(async () => {
    // Prevent duplicate instances: stop any existing agent before creating a new one
    if (voiceAgentRef.current) {
      voiceAgentRef.current.stop();
      voiceAgentRef.current = null;
    }

    setStatus('connecting');
    setErrorMessage(null);

    try {
      const agent = new GlydeVoice({
        ...config,
        onEvent: handleVoiceEvent,
        onTranscript: handleTranscript
      });

      voiceAgentRef.current = agent;
      await agent.start();

      // Capture metadata from voice config after start
      const serverConfig = agent.getServerConfig();
      if (serverConfig?.metadata) {
        setAgentMetadata(serverConfig.metadata);
      }

      // Capture the resolved language map + initial language after start
      setLanguageConfig(agent.getLanguageConfig());
      setActiveLanguage(agent.getActiveLanguage());

    } catch (error) {
      console.error('[useVoiceAgent] Failed to start:', error);
      setStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'Failed to connect');
    }
  }, [config, handleVoiceEvent, handleTranscript]);

  /**
   * Stop the voice session
   */
  const stop = useCallback(() => {
    voiceAgentRef.current?.stop();
    voiceAgentRef.current = null;
    setStatus('idle');
    setIsUserSpeaking(false);
    setIsAgentSpeaking(false);
    setLanguageConfig(null);
    setActiveLanguage(null);
  }, []);

  /**
   * Toggle mute state
   */
  const toggleMute = useCallback(() => {
    const newMuted = !isMuted;
    setIsMuted(newMuted);
    voiceAgentRef.current?.setMuted(newMuted);
  }, [isMuted]);

  /**
   * Set mute state directly
   */
  const setMutedState = useCallback((muted: boolean) => {
    setIsMuted(muted);
    voiceAgentRef.current?.setMuted(muted);
  }, []);

  /**
   * Set the spoken language ('auto' or a configured language code)
   */
  const setLanguage = useCallback((mode: 'auto' | string) => {
    voiceAgentRef.current?.setLanguage(mode);
    // Optimistically reflect the pinned language; 'language_changed' will
    // confirm (or correct) it from the SDK.
    setActiveLanguage(mode === 'auto' ? null : mode);
  }, []);

  /**
   * Clear transcripts
   */
  const clearTranscripts = useCallback(() => {
    setTranscripts([]);
  }, []);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      voiceAgentRef.current?.stop();
    };
  }, []);

  /**
   * Auto-start if configured
   */
  useEffect(() => {
    if (config.autoStart && status === 'idle') {
      start();
    }
  }, [config.autoStart, status, start]);

  return {
    status,
    transcripts,
    isUserSpeaking,
    isAgentSpeaking,
    isMuted,
    errorMessage,
    agentMetadata,
    languageConfig,
    activeLanguage,
    start,
    stop,
    toggleMute,
    setMuted: setMutedState,
    setLanguage,
    clearTranscripts,
    voiceAgent: voiceAgentRef.current,
  };
}
